import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Sparkles, Copy, Check, Printer } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import Navigation from '../components/Navigation';
import LoadingSpinner from '../components/LoadingSpinner';
import AiDisclaimer from './shared/AiDisclaimer';
import { parseAnalyticsResponse } from '../utils/parseAnalyticsResponse';

// ── Formatters ────────────────────────────────────────────────────────────────
const fmtMoney = (val) => {
  if (val == null) return '—';
  const n = Number(val);
  if (isNaN(n) || n === 0) return '—';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000)     return `$${(n / 1_000).toFixed(1)}K`;
  return `$${Math.round(n).toLocaleString()}`;
};

const fmtPct = (val) => (val == null ? '—' : `${Number(val).toFixed(1)}%`);

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }); }
  catch { return ''; }
};

// ── Axaris Signature — four-corner unified exposure card ──────────────────────
const AxarisSignature = ({ monteCarlo, exposurePath, trajectory, fairEAL: fairEALProp }) => {
  const p90 = monteCarlo?.p90;
  const p99 = monteCarlo?.p99;
  // Fall back to MC mean if FAIR EAL not available (e.g. when risk data missing)
  const fairEAL = fairEALProp ?? monteCarlo?.mean ?? p90;
  const pathProb = exposurePath?.overallPathProbability;
  const pathCiLow  = exposurePath?.overallPathProbabilityLow;
  const pathCiHigh = exposurePath?.overallPathProbabilityHigh;
  const openRisks  = exposurePath?.openRisks;
  const totalRisks = exposurePath?.totalRisks ?? exposurePath?.totalRisksAnalyzed;
  const velocity   = trajectory?.remediationVelocityPerMonth;
  const day90Score = trajectory?.predictive90DayScore;

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6 shadow-sm">
      {/* Central headline */}
      <div className="text-center mb-6">
        <div className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1">
          Annual Financial Exposure
        </div>
        <div className="text-5xl font-bold text-emerald-700 tabular-nums leading-none">
          {fmtMoney(fairEAL)}
        </div>
        <div className="text-sm text-gray-400 mt-1">Expected Annual Loss (FAIR-Aligned)</div>
      </div>

      {/* Four-corner grid */}
      <div className="grid grid-cols-2 gap-3">
        {/* Attack Path */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-blue-600 mb-2">
            Attack Path Probability
          </div>
          <div className="text-2xl font-bold text-blue-800 tabular-nums">
            {fmtPct(pathProb)}
          </div>
          {pathCiLow != null && (
            <div className="text-xs text-blue-500 mt-0.5">
              90% CI: {fmtPct(pathCiLow)} – {fmtPct(pathCiHigh)}
            </div>
          )}
        </div>

        {/* Financial Risk */}
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-emerald-600 mb-2">
            Financial Risk Range
          </div>
          <div className="text-2xl font-bold text-emerald-800 tabular-nums">
            {fmtMoney(fairEAL)}
          </div>
          <div className="text-xs text-emerald-500 mt-0.5">
            90% CI: {fmtMoney(monteCarlo?.p5)} – {fmtMoney(monteCarlo?.p95)}
          </div>
        </div>

        {/* Risk Posture */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-red-600 mb-2">
            Risk Posture
          </div>
          <div className="text-2xl font-bold text-red-800 tabular-nums">
            {openRisks != null ? `${openRisks} open` : '—'}
          </div>
          <div className="text-xs text-red-500 mt-0.5">
            {totalRisks != null ? `of ${totalRisks} total risks` : ''}
          </div>
        </div>

        {/* Remediation Velocity */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-green-600 mb-2">
            Remediation Velocity
          </div>
          <div className="text-2xl font-bold text-green-800 tabular-nums">
            {velocity != null ? `${velocity}/mo` : day90Score != null ? `${day90Score}%` : '—'}
          </div>
          <div className="text-xs text-green-500 mt-0.5">
            {velocity != null ? 'closures per month' : '90-day risk score'}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Top Intervention callout ──────────────────────────────────────────────────
const TopInterventionCard = ({ exposurePath }) => {
  const top = exposurePath?.topInterventions?.[0];
  if (!top) return null;

  const effortLabel = top.effortTier === 1 ? 'Quick Win' : top.effortTier === 2 ? 'Standard' : 'Complex';
  const effortColor = top.effortTier === 1 ? 'text-green-700 bg-green-50 border-green-200'
                    : top.effortTier === 2 ? 'text-yellow-700 bg-yellow-50 border-yellow-200'
                    : 'text-red-700 bg-red-50 border-red-200';

  return (
    <div className="bg-white border-l-4 border-orange-500 rounded-r-xl p-5 mb-6 shadow-sm">
      <div className="text-xs font-semibold uppercase tracking-wide text-orange-600 mb-2">
        Recommended Priority Action
      </div>
      <div className="text-base font-semibold text-gray-900 mb-2">{top.riskTitle}</div>
      <div className="flex flex-wrap gap-3 text-sm">
        <span className="text-gray-600">
          Exposure reduction: <span className="font-semibold text-gray-900">{fmtMoney(top.exposureReductionEstimate)}</span>
        </span>
        <span className="text-gray-600">
          Effort: <span className="font-semibold text-gray-900">{Number(top.estimatedFteHours).toFixed(0)} FTE-hours</span>
        </span>
        <span className={`text-xs border rounded px-2 py-0.5 font-medium ${effortColor}`}>
          {effortLabel}
        </span>
        {top.requiresVendorCooperation && (
          <span className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded px-2 py-0.5">
            Vendor cooperation required
          </span>
        )}
      </div>
    </div>
  );
};

// ── Kill-chain phase summary (condensed) ─────────────────────────────────────
const KillChainSummary = ({ exposurePath }) => {
  const stages = exposurePath?.stages;
  if (!stages?.length) return null;

  const COLORS = {
    'Pre-Attack':    'border-blue-200   bg-blue-50   text-blue-700',
    'During Attack': 'border-orange-200 bg-orange-50 text-orange-700',
    'Post-Attack':   'border-red-200    bg-red-50    text-red-700',
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6 shadow-sm">
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-4">
        Attack Chain Coverage
      </div>
      <div className="flex flex-col sm:flex-row gap-3">
        {[...stages].sort((a, b) => {
          const rank = (name = '') => {
            const n = name.toLowerCase();
            if (n.includes('|')) return 99;
            if (n.includes('pre')) return 1;
            if (n.includes('active') || n.includes('during')) return 2;
            if (n.includes('post')) return 3;
            return 50;
          };
          return rank(a.stageName) - rank(b.stageName);
        }).map((s, i, arr) => (
          <React.Fragment key={s.stageName}>
            <div className={`flex-1 border rounded-lg p-3 ${COLORS[s.stageName] ?? 'border-gray-200 bg-gray-50 text-gray-700'}`}>
              <div className="text-xs font-bold uppercase tracking-wide mb-1">{s.stageName}</div>
              <div className="text-xl font-bold tabular-nums">{fmtPct(s.stageTraversalProbability)}</div>
              <div className="text-xs mt-1 opacity-75">
                {s.openRiskCount} open · {fmtMoney(s.stageFinancialImpact)}
              </div>
            </div>
            {i < arr.length - 1 && (
              <div className="hidden sm:flex items-center text-gray-300 text-xl flex-shrink-0">→</div>
            )}
          </React.Fragment>
        ))}
      </div>
      <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between text-sm">
        <span className="text-gray-500">End-to-end attack probability</span>
        <span className="font-bold text-gray-900">{fmtPct(exposurePath.overallPathProbability)}</span>
      </div>
    </div>
  );
};

// ── Narrative section ─────────────────────────────────────────────────────────
const AUDIENCE_TABS = [
  { key: 'ciso',  label: 'CISO'  },
  { key: 'cfo',   label: 'CFO'   },
  { key: 'cio',   label: 'CIO'   },
  { key: 'ceo',   label: 'CEO'   },
  { key: 'board', label: 'Board' },
];
const REASONING_TABS = [
  false && { key: 'counterfactual', label: 'Counterfactual' },
  { key: 'anomaly',        label: 'Anomaly'   },
  { key: 'summary30d',     label: '30-Day'    },
].filter(Boolean);

const CopyBtn = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { /* denied */ }
  };
  return (
    <button onClick={copy} className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700">
      {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
};

const NarrativeSection = ({ assessmentId }) => {
  const apiCall = useApi();
  const [tab, setTab] = useState('board');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const cacheRef = useRef({});

  const fetchNarrative = useCallback(async (voice) => {
    if (cacheRef.current[voice]) {
      setData(cacheRef.current[voice]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const result = await apiCall('POST', `/ransomwareAssessments/${assessmentId}/generate-narrative?voice=${voice}`);
      if (result && result.narrative != null) {
        cacheRef.current[voice] = result;
        setData(result);
      }
    } catch (e) {} finally {
      setLoading(false);
    }
  }, [assessmentId, apiCall]);

  // Auto-fetch default voice on mount
  useEffect(() => {
    if (assessmentId) fetchNarrative('board');
  }, [assessmentId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTabClick = (voice) => {
    setTab(voice);
    fetchNarrative(voice);
  };

  const handleRegenerate = () => {
    delete cacheRef.current[tab];
    setData(null);
    fetchNarrative(tab);
  };

  const text = data?.narrative ?? '';
  const hasNarrative = data != null;

  return (
    <div className="bg-white border border-gray-200 rounded-xl mb-6 overflow-hidden shadow-sm">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-purple-500" />
          <span className="text-sm font-semibold text-gray-900">Intelligence Narrative</span>
          {data?.generatedAtUtc && (
            <span className="text-xs text-gray-400">Generated {fmtDate(data.generatedAtUtc)}</span>
          )}
        </div>
        <button
          onClick={handleRegenerate}
          disabled={loading}
          className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-medium transition-colors
            ${loading ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : hasNarrative ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              : 'bg-purple-600 text-white hover:bg-purple-700'}`}
        >
          {loading
            ? <><RefreshCw className="h-3 w-3 animate-spin" /> Generating...</>
            : hasNarrative ? <><RefreshCw className="h-3 w-3" /> Regenerate</>
            : <><Sparkles className="h-3 w-3" /> Generate</>}
        </button>
      </div>

      {!hasNarrative && !loading && (
        <div className="px-5 py-6 text-center text-sm text-gray-400">
          Generate an AI briefing for CISO, CFO, CIO, CEO, and Board audiences.
        </div>
      )}

      {loading && !hasNarrative && (
        <div className="px-5 py-6 text-center text-sm text-gray-400">
          <RefreshCw className="h-5 w-5 text-purple-400 animate-spin mx-auto mb-2" />
          Generating briefing…
        </div>
      )}

      {(hasNarrative || loading) && (
        <>
          {/* Chrome-style tab bar — Audience Briefings | Analysis */}
          <div style={{ background: '#f1f3f4', borderBottom: '1px solid #e0e0e0', paddingLeft: 12, paddingRight: 12, paddingTop: 8, display: 'flex', alignItems: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
            {AUDIENCE_TABS.map(t => {
              const isActive = tab === t.key;
              return (
                <button key={t.key} onClick={() => handleTabClick(t.key)}
                  style={{ padding: '7px 16px', fontSize: 13, fontWeight: isActive ? 600 : 400, color: isActive ? '#1a1a1a' : '#5f6368', background: isActive ? '#ffffff' : 'transparent', border: isActive ? '1px solid #e0e0e0' : '1px solid transparent', borderBottom: isActive ? '1px solid #ffffff' : '1px solid transparent', borderRadius: '8px 8px 0 0', cursor: 'pointer', marginBottom: isActive ? -1 : 0, position: 'relative', zIndex: isActive ? 1 : 0, whiteSpace: 'nowrap', transition: 'background 0.15s' }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#e8eaed'; }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                >
                  {t.label}
                  {loading && tab === t.key && (
                    <RefreshCw className="inline h-3 w-3 ml-1 animate-spin" style={{ verticalAlign: 'middle' }} />
                  )}
                </button>
              );
            })}
            <div style={{ width: 1, height: 20, background: '#d0d0d0', margin: '0 4px 4px' }} />
            <span style={{ fontSize: 10, color: '#9aa0a8', marginBottom: 6, marginRight: 2, whiteSpace: 'nowrap' }}>Analysis</span>
            {REASONING_TABS.map(t => {
              const isActive = tab === t.key;
              return (
                <button key={t.key} onClick={() => handleTabClick(t.key)}
                  style={{ padding: '7px 14px', fontSize: 13, fontWeight: isActive ? 600 : 400, color: isActive ? '#1a1a1a' : '#7c3aed', background: isActive ? '#ffffff' : 'transparent', border: isActive ? '1px solid #e0e0e0' : '1px solid transparent', borderBottom: isActive ? '1px solid #ffffff' : '1px solid transparent', borderRadius: '8px 8px 0 0', cursor: 'pointer', marginBottom: isActive ? -1 : 0, position: 'relative', zIndex: isActive ? 1 : 0, whiteSpace: 'nowrap', transition: 'background 0.15s' }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#ede9fe'; }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                >
                  {t.label}
                  {loading && tab === t.key && (
                    <RefreshCw className="inline h-3 w-3 ml-1 animate-spin" style={{ verticalAlign: 'middle' }} />
                  )}
                </button>
              );
            })}
          </div>

          <div className="px-5 py-4">
            {loading ? (
              <div className="text-center text-sm text-gray-400 py-4">
                <RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2 text-purple-400" />
                Generating briefing…
              </div>
            ) : text ? (
              <div className="flex gap-2">
                <p className="flex-1 text-sm text-gray-700 leading-relaxed">{text}</p>
                <CopyBtn text={text} />
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic">
                Narrative unavailable for this view. Click Regenerate to retry.
              </p>
            )}
          </div>

          <AiDisclaimer />
        </>
      )}
    </div>
  );
};

// ── Monte Carlo summary ───────────────────────────────────────────────────────
const FinancialSummaryCard = ({ monteCarlo }) => {
  if (!monteCarlo) return null;
  const bands = [
    { label: 'P50',              key: 'p50', color: 'bg-blue-300'   },
    { label: 'P90  Stress',      key: 'p90', color: 'bg-orange-500' },
    { label: 'P95',              key: 'p95', color: 'bg-red-400'    },
  ];
  const maxVal = Number(monteCarlo.p95) || Number(monteCarlo.p90) || 1;

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6 shadow-sm">
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-4">
        Portfolio Loss Distribution
        <span className="ml-2 font-normal text-gray-400">
          {(monteCarlo.iterationCount ?? 0).toLocaleString()} simulations
          · {monteCarlo.risksWithCompleteFinancials ?? 0}/{monteCarlo.riskCount ?? 0} risks
        </span>
      </div>
      <div className="space-y-2">
        {bands.map(({ label, key, color }) => {
          const val = monteCarlo[key];
          const pct = Math.min(100, (Number(val) / maxVal) * 100);
          return (
            <div key={key} className="flex items-center gap-3 rounded">
              <span className="w-28 text-xs shrink-0 text-gray-500">{label}</span>
              <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                <div className={`${color} h-1.5 rounded-full`} style={{ width: `${pct}%` }} />
              </div>
              <span className="w-20 text-right text-sm tabular-nums text-gray-700">
                {fmtMoney(val)}
              </span>
            </div>
          );
        })}
      </div>
      <div className="mt-3 pt-3 border-t border-gray-100 flex justify-between text-xs text-gray-500">
        <span>Mean: <span className="font-medium text-gray-800">{fmtMoney(monteCarlo.mean)}</span></span>
        <span>Std Dev: <span className="font-medium text-gray-800">{fmtMoney(monteCarlo.standardDeviation)}</span></span>
        {/* The FAIR summary carries no observed maximum, so "Worst" only shows when one is supplied. */}
        {typeof monteCarlo.maxObserved === 'number' && (
          <span>Worst: <span className="font-medium text-red-600">{fmtMoney(monteCarlo.maxObserved)}</span></span>
        )}
      </div>
    </div>
  );
};

// ── Main executive view ───────────────────────────────────────────────────────
const RansomwareExecutiveView = () => {
  const { id }     = useParams();
  const navigate   = useNavigate();
  const apiCall    = useApi();

  const [assessment,       setAssessment]       = useState(null);
  const [monteCarlo,       setMonteCarlo]        = useState(null);
  const [risks,            setRisks]             = useState([]);
  const [exposurePath,     setExposurePath]      = useState(null);
  const [trajectory,       setTrajectory]        = useState(null);
  const [loading,          setLoading]           = useState(true);

  useEffect(() => {
    if (!id) return;

    const load = async () => {
      setLoading(true);
      try {
        const [assessmentData, analyticsData, pathData, trajectoryData] = await Promise.allSettled([
          apiCall('GET', `/ransomwareAssessments/${id}`),
          apiCall('GET', `/ransomwareRisks/assessment-risks/${id}`),
          apiCall('GET', `/ransomwareRisks/assessment-risks/${id}/exposure-path`),
          apiCall('GET', `/ransomwareRisks/ransomwareAssessment/${id}/predictive-risk`),
        ]);

        if (assessmentData.status === 'fulfilled') setAssessment(assessmentData.value);

        if (analyticsData.status === 'fulfilled') {
          // The endpoint returns { risks, quantificationFactors }; there is no monteCarlo key, so the
          // FAIR simulation is mapped into the shape these cards read, as RansomwareAssessmentRisksView does.
          const { risks: risksList, quantificationFactors: qf } = parseAnalyticsResponse(analyticsData.value);
          setMonteCarlo(qf ? {
            p5: qf.mcP5,
            p50: qf.mcP50,
            p90: qf.mcP90,
            p95: qf.mcP95,
            p99: qf.mcP99,
            mean: qf.mcMean,
            standardDeviation: qf.mcStdDev,
            iterationCount: qf.iterations,
            riskCount: qf.totalRiskCount,
            risksWithCompleteFinancials: qf.scenarioCount,
          } : null);
          // Extract risks array for FAIR EAL computation
          setRisks(risksList);
        }

        if (pathData.status === 'fulfilled') setExposurePath(pathData.value);

        if (trajectoryData.status === 'fulfilled') {
          setTrajectory(trajectoryData.value);
        }
      } catch (e) {} finally {
        setLoading(false);
      }
    };

    load();
  }, [id]);

  if (loading) return <LoadingSpinner />;

  const assessmentName = assessment?.context?.applicationName
    ?? assessment?.dealName
    ?? `Assessment ${id}`;
  const confidenceLevel = exposurePath?.confidenceLevel ?? 'Moderate';

  // Compute FAIR EAL from risks (sum impliedExpectedAnnualContribution for open risks)
  const fairEAL = risks.reduce((sum, r) => {
    const s = (r.ransomwareStatus ?? r.status ?? '').toLowerCase();
    if (s === 'closed' || s === 'remediated') return sum;
    const impact = r.impliedExpectedAnnualContribution ?? r.finalCalculatedMonetaryImpact;
    return impact != null && Number(impact) > 0 ? sum + Number(impact) : sum;
  }, 0) || null;

  return (
    <div className="min-h-screen bg-gray-50 print:bg-white">
      <Navigation />

      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 print:py-4">

        {/* Header */}
        <div className="flex items-start justify-between mb-6 print:mb-4">
          <div>
            <button
              onClick={() => navigate(`/RansomwareAssessment/${id}/risks`)}
              className="flex items-center text-sm text-gray-500 hover:text-gray-800 mb-2 print:hidden"
            >
              <ArrowLeft className="h-4 w-4 mr-1" /> Back to Risk Detail
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Ransomware Intelligence Brief</h1>
            <p className="text-sm text-gray-500 mt-0.5">{assessmentName}</p>
          </div>
          <div className="flex items-center gap-3 print:hidden">
            <span className={`text-xs border rounded px-2 py-1 font-medium
              ${confidenceLevel === 'High'     ? 'bg-green-50 text-green-700 border-green-300'
              : confidenceLevel === 'Moderate' ? 'bg-yellow-50 text-yellow-700 border-yellow-300'
              : 'bg-gray-100 text-gray-600 border-gray-300'}`}>
              {confidenceLevel} Confidence
            </span>
            <button
              onClick={() => window.print()}
              className="flex items-center gap-1.5 text-sm px-3 py-1.5 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50"
            >
              <Printer className="h-4 w-4" /> Print
            </button>
          </div>
        </div>

        {/* Axaris Signature — four-corner unified exposure view */}
        <AxarisSignature
          monteCarlo={monteCarlo}
          exposurePath={exposurePath}
          trajectory={trajectory}
          fairEAL={fairEAL}
        />

        {/* Narrative */}
        <NarrativeSection assessmentId={id} />

        {/* Top Intervention */}
        <TopInterventionCard exposurePath={exposurePath} />

        {/* Kill-chain summary */}
        <KillChainSummary exposurePath={exposurePath} />

        {/* Financial distribution */}
        <FinancialSummaryCard monteCarlo={monteCarlo} />

        {/* Footer */}
        <div className="text-xs text-gray-400 text-center mt-4 print:mt-2">
          Financial figures computed by Axaris v3.1 FAIR-Aligned financial engine.
          AI narratives generated from pre-computed data — AI does not produce financial estimates.
        </div>
      </div>
    </div>
  );
};

export default RansomwareExecutiveView;
